import * as React from 'react'
import { PropsWithChildren, ReactElement } from 'react'
import { DefaultButton, Depths, FontWeights, IconButton, Stack, StackItem, Text } from '@fluentui/react'
import { FormikHelpers } from 'formik/dist/types'
import { Form, Formik, useFormikContext } from 'formik'
import { useBoolean } from '@fluentui/react-hooks'
import useSubmitDocumentGenerationRequest from '../../../../hooks/finance/report/useSubmitDocumentGenerationRequest'
import {
    DocumentGenerationParams,
    DocumentGenerationType,
} from '../../../../hooks/document/useDocumentGenerationHandler'

type Props<ParamsT> = PropsWithChildren<{
    open?: boolean
    title: string
    downloadText?: string
    generationType: DocumentGenerationType
    initialValues?: Omit<DocumentGenerationParams<ParamsT>, 'generationType'>
}>

type Values<ParamsT> = Props<ParamsT>['initialValues']

export enum ReportPeriod {
    Day = 'DAY',
    Week = 'WEEK',
    Month = 'MONTH',
    Year = 'YEAR',
}

function ReportCard<ParamsT>(props: Props<DocumentGenerationParams<ParamsT>>): ReactElement {
    const { submitForm } = useFormikContext<DocumentGenerationParams<ParamsT>>()
    const [open, { toggle: toggleOpen }] = useBoolean(props.open || false)
    return (
        <Stack tokens={{ padding: 's1', childrenGap: 's1' }} styles={{ root: { boxShadow: Depths.depth4 } }} grow>
            <Stack horizontal tokens={{ childrenGap: 's2' }}>
                {props.children && (
                    <StackItem>
                        <IconButton
                            iconProps={{ iconName: open ? 'ChevronDown' : 'ChevronRight' }}
                            onClick={toggleOpen}
                            styles={{
                                root: { height: 24, width: 24 },
                                icon: { height: 12, lineHeight: 12, fontSize: 12 },
                            }}
                        />
                    </StackItem>
                )}
                <StackItem grow>
                    <Text variant="large" styles={{ root: { fontWeight: FontWeights.regular } }} block={false}>
                        {props.title}
                    </Text>
                </StackItem>
            </Stack>
            <StackItem grow>{(open && props.children) || <span />}</StackItem>
            <StackItem>
                <DefaultButton
                    primary
                    iconProps={{ iconName: 'Installation' }}
                    text={props.downloadText || 'Download'}
                    onClick={() => submitForm()}
                    styles={{ root: { width: '100%' } }}
                />
            </StackItem>
        </Stack>
    )
}

export default function ReportForm<ParamsT>(props: Props<ParamsT>): ReactElement {
    const submitRequest = useSubmitDocumentGenerationRequest<ParamsT>(props.generationType)

    return (
        <Formik<Values<ParamsT>>
            initialValues={{
                generationType: props.generationType,
                ...props.initialValues,
            }}
            onSubmit={(values: Values<ParamsT>, formikHelpers: FormikHelpers<Values<ParamsT>>) => {
                // Values and Omit<ReportParamsDto, 'state'> are actually the same, but the TS compiler doesn't
                // accurately pick up on it.
                submitRequest(values)
                formikHelpers.setSubmitting(false)
            }}
        >
            <Form noValidate style={{ display: 'flex', flex: 1, minWidth: 240, maxWidth: 360 }}>
                <ReportCard {...props} />
            </Form>
        </Formik>
    )
}
